import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { MergeSummary } from '@app/pages/expedition/create/mappers/mergeToForm.ts';

interface ComponentProps {
  summary: MergeSummary;
  dataTestId?: string;
}

const COMBINED_LABELS: Record<MergeSummary['combined'][number]['field'], [string, string]> = {
  labels: ['CreateExpedition.mergeCombinedLabels', 'Labels'],
  attachments: ['CreateExpedition.mergeCombinedAttachments', 'Documents'],
  notifiedTargets: ['CreateExpedition.mergeCombinedNotifiedTargets', 'Notified people'],
  specialRequirements: ['CreateExpedition.mergeCombinedSpecialRequirements', 'Special requirements'],
  customsDeclarationCompanies: ['CreateExpedition.mergeCombinedCustomsCompanies', 'Customs declarants'],
  folders: ['CreateExpedition.mergeCombinedFolders', 'Folders'],
};

/**
 * What the merge managed to do, in the same words in the form and in the confirm dialog.
 *
 * Shown next to the warnings on purpose: a list of only what went wrong reads as though nothing
 * went right, and the user is about to close other people's shipments on the strength of it.
 */
const MergeSummaryList: React.FC<ComponentProps> = ({ summary, dataTestId }) => {
  const { tr } = useTranslationLgs();

  return (
    <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'} data-testid={dataTestId}>
      <li>{tr('CreateExpedition.mergeSummaryShipments', '{{count}} shipments become one', { count: summary.shipmentCount })}</li>

      <li>
        {summary.foldedStopCount > 0
          ? tr('CreateExpedition.mergeSummaryStopsFolded', '{{stops}} stops on the route, after combining {{folded}} repeated visits to the same place', {
              stops: summary.stopCount,
              folded: summary.foldedStopCount,
            })
          : tr('CreateExpedition.mergeSummaryStops', '{{stops}} stops on the route', { stops: summary.stopCount })}
      </li>

      {summary.orderCount > 0 && <li>{tr('CreateExpedition.mergeSummaryOrders', '{{count}} orders carried over', { count: summary.orderCount })}</li>}

      {summary.combined.map(entry => {
        const [key, fallback] = COMBINED_LABELS[entry.field];
        return (
          <li key={entry.field}>
            {tr(key, fallback)}: {tr('CreateExpedition.mergeSummaryCombined', '{{count}} kept from all shipments', { count: entry.count })}
          </li>
        );
      })}

      {summary.distribution && (
        <li>
          {summary.distribution.selectedCarrier
            ? tr('CreateExpedition.mergeSummaryCarrierKept', 'Assigned directly to {{carrier}}, as all the shipments were', {
                carrier: summary.distribution.selectedCarrier,
              })
            : tr('CreateExpedition.mergeSummaryCarrierChoice', 'Assigned directly, but the shipments had different carriers — pick one: {{carriers}}', {
                carriers: summary.distribution.carriers.join(', '),
              })}
        </li>
      )}

      {summary.distribution?.prices.length > 0 && (
        <li>
          {summary.distribution.totalPrice
            ? tr('CreateExpedition.mergeSummaryPriceTotal', 'Price {{total}}, the sum of {{prices}}', {
                total: summary.distribution.totalPrice,
                prices: summary.distribution.prices.join(' + '),
              })
            : tr('CreateExpedition.mergeSummaryPriceCurrencies', 'Prices were in different currencies and were left empty: {{prices}}', {
                prices: summary.distribution.prices.join(', '),
              })}
        </li>
      )}
    </ul>
  );
};

export default MergeSummaryList;
